import dayjs from "dayjs";
import { oids } from "@/configs/Settings";
import {
  invitePatientDisplay,
  inviteStatusLabel,
  isInvitePending,
} from "../../invites/helpers/inviteTaskHelpers";
import { resolveOrganizationDisplays } from "../../services/resolveOrganizationDisplays";
import type { ProviderInviteRow } from "../types";

/**
 * `PractitionerRole/<cnpj>-<oid.cpf>-<cpf>` → `<oid.cnpj>-<cnpj>`.
 * O oid do CPF não tem hífen, então o CNPJ é o trecho antes do primeiro hífen.
 */
function organizationTokenFromRoleReference(roleReference?: string): string {
  const roleId = roleReference?.startsWith("PractitionerRole/")
    ? roleReference.split("/")[1] || ""
    : "";
  const cnpj = roleId.split("-")[0] || "";
  return cnpj ? `${oids.cnpj}-${cnpj}` : "";
}

/**
 * Tasks de convite (para os profissionais da prestadora) → linhas, com o nome
 * da organização que convidou. Pendentes primeiro; depois os mais recentes.
 */
export async function toProviderInviteRows(tasks: fhir5.Task[]): Promise<ProviderInviteRow[]> {
  const requesterTokens = tasks.map((task) =>
    organizationTokenFromRoleReference(task.requester?.reference),
  );
  const organizationNames = await resolveOrganizationDisplays(requesterTokens);
  const now = dayjs();

  return tasks
    .filter((task) => Boolean(task.id))
    .map((task, taskIndex) => ({
      taskId: task.id as string,
      guestName: task.for?.display || "Profissional",
      guestReference: task.for?.reference,
      consultationDate: task.restriction?.period?.end || task.authoredOn,
      requesterName: task.requester?.display || "Profissional",
      requesterOrganizationName:
        organizationNames.get(requesterTokens[taskIndex]) || "Organização não identificada",
      patientName: invitePatientDisplay(task),
      statusLabel: inviteStatusLabel(task, now),
      isPending: isInvitePending(task, now),
      authoredOn: task.authoredOn,
      deadline: task.restriction?.period?.end,
    }))
    .sort((first, second) => {
      if (first.isPending !== second.isPending) return first.isPending ? -1 : 1;
      return dayjs(second.authoredOn).valueOf() - dayjs(first.authoredOn).valueOf();
    });
}
